import {
    ChildListWrapper,
    ListContent,
    ListWrapper,
    StyledDivider,
    StyledList,
    StyledToggleButton,
} from "./AccessGroupPageAccessPage.styles";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AccessGroupPageAccessManagePermissions from "./AccessGroupPageAccessManagePermissions";
import BlockHead from "shared/components/layout/heads/BlockHead";
import HStack from "shared/components/layout/stack/HStack";
import Modal from "shared/components/feedback/modals/MasterModal";
import PropTypes from "prop-types";
import SectionHead from "shared/components/layout/heads/SectionHead";
import TertiaryButton from "shared/components/inputs/button/TertiaryButton";
import VStack from "shared/components/layout/stack/VStack";
import { sendNotification } from "shared/components/feedback/notification/notification";
import useAccessGroupApi from "features/admin/access-groups/single/useAccessGroup";
import useAccessGroupPageAccessApi from "features/admin/access-groups/single/useAccessGroupPageAccess";

const FormPresenter = () => {
    const { groupId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const { useAccessGroupPageAccess } = useAccessGroupPageAccessApi();

    const pageAccessData = useAccessGroupPageAccess(groupId);

    const action = searchParams.get("action");
    const pageId = +searchParams.get("pageId");
    
    const onClose = () => {
        navigate(".", { replace: true });
    };

    if (action === "manageActionPermissions") {
        return (
            <Modal
                width={512}
                title="Manage Action Permissions"
                description={
                    pageAccessData.data?.find((a) => a.pageId === pageId).pageName
                }
                onRequestClose={onClose}
            >
                <AccessGroupPageAccessManagePermissions
                    groupId={groupId}
                    pageId={pageId}
                />
            </Modal>
        );
    }

    return <></>;
};

const ListContentCard = ({
    pageName,
    pageDescription,
    enabled,
    hasActions,
    pageId,
}) => {
    return (
        <ListContent>
            <BlockHead title={pageName} description={pageDescription} />
            {enabled && hasActions && (
                <TertiaryButton
                    leadingIcon="filter"
                    label="Manage Action Permission"
                    to={`?action=manageActionPermissions&pageId=${pageId}`}
                    replace={true}
                />
            )}
        </ListContent>
    );
};

const AccessGroupPageAccessPage = () => {
    const { groupId } = useParams();

    const { useAccessGroup } = useAccessGroupApi();
    const {
        useAccessGroupPageAccess,
        createAccessGroupPage,
        deleteAccessGroupPage,
    } = useAccessGroupPageAccessApi();

    const pageAccessData = useAccessGroupPageAccess(groupId);

    const groupData = useAccessGroup(groupId);

    const handleChange = async (pageId, enabled) => {
        const modifier = enabled
            ? deleteAccessGroupPage
            : createAccessGroupPage;

        const allData = { groupId: groupId, pageId: pageId };

        await modifier.mutateAsync(allData);
        sendNotification(
            <>
                Page Access Permission for{" "}
                <strong> {groupData.data.name} </strong> have been updated
            </>
        );
    };

    const pageContent = pageAccessData.error ? (
        <>Error</>
    ) : (
        <VStack spacing="Two">
            <SectionHead
                title="Page Access permissions"
                description="Manage the pages this Access Group has permission to access"
            />
            <HStack spacing="Two">
                <StyledList>
                    {(pageAccessData.data ?? [])
                        .filter(
                            (parentData) =>
                                parentData.pageParentId === null
                        )
                        .map((data) => (
                            <VStack
                                spacing="Zero"
                                key={data.pageId}
                                style={{ padding: 0 }}
                            >
                                <ListWrapper>
                                    <ListContentCard {...data} />
                                    <StyledToggleButton
                                        defaultChecked={data.enabled}
                                        onChange={() =>
                                            handleChange(
                                                data.pageId,
                                                data.enabled
                                            )
                                        }
                                        label={["Off", "On"]}
                                    />
                                </ListWrapper>
                                <StyledDivider />
                                {(pageAccessData.data ?? [])
                                    .filter(
                                        (childData) =>
                                            childData.pageParentId ===
                                            data.pageId
                                    )
                                    .map((data) => (
                                        <ChildListWrapper key={data.pageId}>
                                            <ListWrapper>
                                                <ListContentCard
                                                    {...data}
                                                />
                                                <StyledToggleButton
                                                    defaultChecked={
                                                        data.enabled
                                                    }
                                                    onChange={() =>
                                                        handleChange(
                                                            data.pageId,
                                                            data.enabled
                                                        )
                                                    }
                                                    label={[
                                                        "Off",
                                                        "On",
                                                    ]}
                                                />
                                            </ListWrapper>
                                            <StyledDivider />
                                        </ChildListWrapper>
                                    ))}
                            </VStack>
                        ))}
                </StyledList>
            </HStack>
        </VStack>
    );

    return (
        <>
            <FormPresenter />
            {pageAccessData.isLoading ? <>Loading...</> : pageContent}
        </>
    );
};

ListContentCard.propTypes = {
    pageName: PropTypes.string,
    pageDescription: PropTypes.string,
    enabled: PropTypes.bool,
    hasActions: PropTypes.bool,
    pageId: PropTypes.number,
};

export default AccessGroupPageAccessPage;
